import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

export default function Footer() {
  const [visitorCount, setVisitorCount] = useState(0);
  const [displayCount, setDisplayCount] = useState(0);

  useEffect(() => {
    const SESSION_KEY = 'site_visited_this_session';

    const fetchAndUpdate = async () => {
      const alreadyVisited = sessionStorage.getItem(SESSION_KEY);

      if (!alreadyVisited) {
        const referrer = document.referrer;
        const currentHost = window.location.hostname;
        const isOrganic = referrer && !referrer.includes(currentHost);
        const hasUtm = window.location.search.includes('utm_');
        const isDirect = !referrer;

        if (isOrganic || hasUtm || isDirect) {
          // 使用 rpc 或直接更新计数
          const { data: current } = await supabase
            .from('visitor_counter')
            .select('count')
            .eq('id', 1)
            .maybeSingle();

          if (current) {
            const newCount = current.count + 1;
            await supabase
              .from('visitor_counter')
              .update({ count: newCount, updated_at: new Date().toISOString() })
              .eq('id', 1);
            setVisitorCount(newCount);
          }
        }
        sessionStorage.setItem(SESSION_KEY, 'true');
      } else {
        // 已经访问过，只读取当前计数
        const { data } = await supabase
          .from('visitor_counter')
          .select('count')
          .eq('id', 1)
          .maybeSingle();

        if (data) {
          setVisitorCount(data.count);
        }
      }
    };

    fetchAndUpdate();
  }, []);

  useEffect(() => {
    if (visitorCount === 0) return;
    const duration = 1500;
    const steps = 40;
    const increment = visitorCount / steps;
    let current = 0;
    let step = 0;

    const timer = setInterval(() => {
      step++;
      current = Math.min(Math.round(increment * step), visitorCount);
      setDisplayCount(current);
      if (step >= steps) {
        clearInterval(timer);
        setDisplayCount(visitorCount);
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [visitorCount]);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-gray-900 text-white py-16 relative overflow-hidden">
      <div className="absolute bottom-0 left-0 right-0 text-[200px] font-bold text-white/5 text-center leading-none pointer-events-none">
        全力以赴
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          <div>
            <img
              src="https://static.readdy.ai/image/eaf594fcd34cc7923c4532bd1fc1545e/0af61fca52f30e20076c2ac20347c83f.jpeg"
              alt="全力以赴企業團建"
              className="h-20 w-auto object-contain mb-4 brightness-0 invert"
            />
            <p className="text-gray-400 text-sm leading-relaxed">
              打造會做事的團隊
            </p>
          </div>

          <div>
            <h4 className="font-bold text-lg mb-4">快速連結</h4>
            <ul className="space-y-3">
              <li>
                <button
                  onClick={() => scrollToSection('about')}
                  className="text-gray-400 hover:text-white transition-colors cursor-pointer"
                >
                  關於我們
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('services')}
                  className="text-gray-400 hover:text-white transition-colors cursor-pointer"
                >
                  服務項目
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('workshop')}
                  className="text-gray-400 hover:text-white transition-colors cursor-pointer"
                >
                  工作坊
                </button>
              </li>
              <li>
                <button
                  onClick={() => scrollToSection('contact')}
                  className="text-gray-400 hover:text-white transition-colors cursor-pointer"
                >
                  聯絡我們
                </button>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-bold text-lg mb-4">追蹤我們</h4>
            <ul className="space-y-3">
              <li>
                <a href="#" className="text-gray-400 hover:text-white transition-colors flex items-center gap-2">
                  <i className="ri-facebook-fill"></i>
                  Facebook
                </a>
              </li>
              <li>
                <a href="#" className="text-gray-400 hover:text-white transition-colors flex items-center gap-2">
                  <i className="ri-instagram-line"></i>
                  Instagram
                </a>
              </li>
              <li>
                <a href="#" className="text-gray-400 hover:text-white transition-colors flex items-center gap-2">
                  <i className="ri-line-fill"></i>
                  LINE
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-bold text-lg mb-4">訂閱最新團建資訊</h4>
            <div className="flex gap-2">
              <input
                type="email"
                placeholder="您的電子郵件"
                className="flex-1 px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
              />
              <button className="w-10 h-10 bg-white rounded-full flex items-center justify-center hover:bg-orange-500 transition-colors cursor-pointer flex-shrink-0">
                <i className="ri-arrow-right-line text-black"></i>
              </button>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-400 text-sm">
            © 2024 全力以赴企業團建有限公司. All rights reserved.
          </p>
          <a
            href="https://readdy.ai/?ref=logo"
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-400 hover:text-white text-sm transition-colors"
          >
            Powered by Readdy
          </a>
        </div>

        {/* 到站瀏覽人數 */}
        <div className="border-t border-white/10 mt-8 pt-6 flex justify-center">
          <div className="flex items-center gap-3 text-gray-400 text-sm">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-group-line text-orange-400"></i>
            </div>
            <span>到站瀏覽人數</span>
            <span className="text-white font-bold text-base tracking-wider tabular-nums">
              {displayCount.toLocaleString()}
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
